/**
 * The two-zone header: identity leading, headline figure trailing, on a shared
 * baseline.
 *
 * `minmax(0, 1fr) auto` rather than a flex chain with a `flex: 1` spacer. A
 * balance spacer is invisible in the markup and decides the layout anyway,
 * which is how a figure ends up drifting away from the title it belongs to
 * when the title happens to be short.
 */
"use client";
import type { ReactElement, ReactNode } from "react";

const HEADLINE_GAP = 12;

interface CresultcardHeadlineProps {
  readonly identity: ReactNode;
  readonly figure: ReactNode;
  /** One line beneath both zones — a scope line, an as-of, a stop summary. */
  readonly meta?: ReactNode;
}

export function CresultcardHeadline({
  identity,
  figure,
  meta,
}: CresultcardHeadlineProps): ReactElement {
  return (
    <div
      data-el="Cresultcard/Headline/Root"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "baseline",
        gap: HEADLINE_GAP,
        minWidth: 0,
      }}
    >
      <div
        data-el="Cresultcard/Headline/Identity"
        style={{ minWidth: 0 }}
      >
        {identity}
      </div>
      <div
        data-el="Cresultcard/Headline/Figure"
        style={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}
      >
        {figure}
      </div>
      {meta != null && (
        <div
          data-el="Cresultcard/Headline/Meta"
          style={{ gridColumn: "1 / -1", minWidth: 0 }}
        >
          {meta}
        </div>
      )}
    </div>
  );
}
